import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  assumptionsOf,
  cardText,
  evidenceSegmentsOf,
  headlineOf,
  regimeOf,
  ResultCardPayload,
  specRowsOf,
  SpecRow,
  SECOND_REVIEW_NOTE,
  STATUS_LABELS,
  trackOf,
  trailOf,
  unusedSpecRowsOf,
} from 'components/ExportDesign/exportDesignResultCardData';

// --- The classification as a card (UX slice S2, design boards 04/05): where the product landed on the
// regime track, the ITAR check, the EAR check with the specs that put it there, what it rests on, what it
// means for licensing, and how we got here. Rendered from `data.result_card` in place of the markdown
// answer, which stays the fallback whenever the card is missing.

const REGIME_CLASS: Record<string, string> = {
  ITAR: 'regime-itar',
  'EAR-listed': 'regime-ear-listed',
  EAR99: 'regime-ear99',
  uncertain: 'regime-uncertain',
};

const STATUS_CLASS: Record<string, string> = {
  checked: 'is-ok',
  provisional: 'is-warn',
  not_reviewed: 'is-neutral',
  preliminary: 'is-neutral',
  revised: 'is-warn',
};

const Markdown: React.FC<{ text: string }> = ({ text }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
);

// `context`: a USML entry a non-ITAR result leans on (why it isn't ITAR), drawn neutral, not red.
const SpecRows: React.FC<{ rows: SpecRow[]; context?: boolean }> = ({ rows, context }) => (
  <div className='export-design-specs'>
    {rows.map((row, index) => (
      <div
        key={`${row.citation}-${index}`}
        className={`export-design-spec${context ? ' is-context' : row.itar ? ' is-itar' : ''}`}
      >
        {/* The clause chip sits under the value, not in a column of its own: a long ITAR citation
            ("22 CFR § 121.1, USML Category XII(a)(1) · SME …") squeezed the explanation to one word
            per line. */}
        <div>
          <div className='export-design-spec-name'>{row.name}</div>
          <div className={`export-design-spec-val${row.valueIsText ? ' is-text' : ''}`}>{row.value}</div>
          <span className='export-design-clause'>
            {row.citation}
            {row.reasons ? ` · ${row.reasons}` : ''}
          </span>
        </div>
        <div>
          {row.why && <div className='export-design-spec-why'>{row.why}</div>}
          {row.clear && (
            <div className='export-design-spec-flip'>
              <span>What would clear it: </span>
              {row.clear}
            </div>
          )}
        </div>
      </div>
    ))}
  </div>
);

interface ExportDesignResultCardProps {
  card: ResultCardPayload;
  /** Puts a sentence starter for an assumption in the composer (nothing is sent). */
  onAnswerAssumption?: (label: string) => void;
  disabled?: boolean;
  /** The card's last row while the run behind it still works (S12 R-3: the Preliminary card). */
  liveFoot?: React.ReactNode;
}

const ExportDesignResultCard: React.FC<ExportDesignResultCardProps> = ({
  card,
  onAnswerAssumption,
  disabled,
  liveFoot,
}) => {
  const regime = regimeOf(card);
  const headline = headlineOf(card);
  const track = trackOf(card);
  const rows = specRowsOf(card);
  const itarRows = rows.filter((row) => row.itar);
  const earRows = rows.filter((row) => !row.itar);
  const unused = unusedSpecRowsOf(card);
  const evidence = evidenceSegmentsOf(card.evidence);
  const assumptions = assumptionsOf(card);
  const orderNote = cardText(card.classification.order_of_review_note);
  const explanation = cardText(card.classification.explanation);
  const candidateEccns = (card.classification.candidate_eccns || []).filter(Boolean);
  const issues = (card.verifier?.issues || []).map((issue) => cardText(issue)).filter(Boolean);
  const confidence = cardText(card.classification.confidence);
  const claim = card.classification.matched_user_claim;
  // The early ITAR answer (S5): its plain reasons explain "not on the USML"; when the final result
  // disagrees, the lane says what changed instead (D-10).
  const screen = card.itar_screen || null;
  const revised = screen?.revised ? cardText(screen.revised).replace(/^Revised:\s*/, '') : '';
  // Only a settled, not-on-the-USML screen supplies the "Not on the USML." reasons (the backend already
  // drops the screen for an uncertain result; this keeps a card that still carries one from saying it).
  const screenReasons =
    screen && !revised && screen.outcome === 'not_on_usml' && regime !== 'uncertain'
      ? screen.reasons.map((r) => cardText(r)).filter(Boolean)
      : [];
  const revisedNote = revised && (
    <div className='export-design-revised'>
      <span className='export-design-pill is-warn'>Revised</span>
      <span>{revised.charAt(0).toUpperCase() + revised.slice(1)}</span>
    </div>
  );

  // Specs that didn't change the result, with the ones close to a limit (S10, board 04).
  const unchanged = unused.rows.filter((row) => !row.nearOnly).length;
  const unusedSummary = [
    unchanged > 0 ? (unchanged === 1 ? "1 spec didn't change the result" : `${unchanged} specs didn't change the result`) : '',
    unused.nearMissCount > 0 ? `${unused.nearMissCount} close to a limit` : '',
  ]
    .filter(Boolean)
    .join(' · ');
  const unusedBlock = unused.rows.length > 0 && (
    <details className='export-design-more export-design-more-inline'>
      <summary>{unusedSummary}</summary>
      <ul className='export-design-unused'>
        {unused.rows.map((spec) => (
          <li key={spec.key}>
            <b>{spec.label}</b>
            {spec.value ? `: ${spec.value}` : ''}
            {spec.nearMiss && (
              <div className='export-design-near-miss'>
                <span className='export-design-near-miss-tag'>Close to a limit</span> {spec.nearMiss}
              </div>
            )}
          </li>
        ))}
      </ul>
    </details>
  );

  // The user's own words that put it here, highlighted only where they appear exactly (S10, board 05).
  const evidenceBlock = evidence.length > 0 && (
    <div className='export-design-inset export-design-evidence'>
      <div className='export-design-evidence-h'>In your words</div>
      <div className='export-design-evidence-text'>
        {evidence.map((segment, index) =>
          segment.hl ? (
            <mark key={index} className='export-design-hl'>
              {segment.text}
            </mark>
          ) : (
            <span key={index} className={segment.gap ? 'export-design-evidence-gap' : undefined}>
              {segment.text}
            </span>
          ),
        )}
      </div>
    </div>
  );

  const assumptionsBlock = assumptions.length > 0 && (
    <section>
      <div className='export-design-section-h'>What this rests on</div>
      <div className='export-design-assume'>
        {assumptions.map((row) => (
          <div key={row.label} className='export-design-assume-row'>
            <span>
              <b>{row.label}.</b> {row.text}
            </span>
            {onAnswerAssumption && (
              <button
                type='button'
                className='standard-workflow-secondary-btn export-design-btn-sm'
                onClick={() => onAnswerAssumption(row.label)}
                disabled={disabled}
              >
                Tell me
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );

  // The ITAR check: what puts it on the USML, or why it isn't there.
  const itarLane =
    regime === 'ITAR' ? (
      <section>
        <div className='export-design-section-h'>ITAR check: what puts it on the US Munitions List</div>
        {revisedNote}
        {evidenceBlock}
        {(itarRows.length > 0 ? itarRows : rows).length > 0 ? (
          <SpecRows rows={itarRows.length > 0 ? itarRows : rows} />
        ) : (
          <div className='export-design-inset export-design-inset-text'>{orderNote || 'Listed on the USML.'}</div>
        )}
        {unusedBlock}
      </section>
    ) : (
      <section>
        <div className='export-design-section-h'>ITAR check: US Munitions List</div>
        {revisedNote}
        {screenReasons.length > 0 ? (
          <div className='export-design-inset export-design-inset-text export-design-screen-reasons'>
            <div>
              <b>Not on the USML.</b>
            </div>
            {screenReasons.map((reason) => (
              <div key={reason}>{reason}</div>
            ))}
            {screen && screen.open_question && (
              <div className='export-design-screen-muted'>{cardText(screen.open_question)}</div>
            )}
          </div>
        ) : (
          <div className='export-design-inset export-design-inset-text'>
            <b>{regime === 'uncertain' ? 'Not settled yet.' : 'Not on the USML.'}</b>
            {orderNote ? ` ${orderNote}` : regime === 'uncertain' ? '' : ' No category covers it.'}
          </div>
        )}
        {/* A USML entry the result leans on without being ITAR (e.g. a note that releases it to the
            CCL). Uncertain results list every control under "Controls it may meet" instead. */}
        {regime !== 'uncertain' && itarRows.length > 0 && earRows.length > 0 && (
          <div className='export-design-lane-more'>
            <SpecRows rows={itarRows} context />
          </div>
        )}
      </section>
    );

  // The EAR check: the specs that put it on the CCL, or why it isn't there, or why it wasn't needed.
  let earLane: React.ReactNode = null;
  if (regime === 'EAR-listed') {
    earLane = (
      <section>
        <div className='export-design-section-h'>EAR check: what puts it on the Commerce Control List</div>
        {evidenceBlock}
        {(earRows.length > 0 ? earRows : rows).length > 0 && (
          <SpecRows rows={earRows.length > 0 ? earRows : rows} />
        )}
        {unusedBlock}
      </section>
    );
  } else if (regime === 'EAR99') {
    earLane = (
      <section>
        <div className='export-design-section-h'>EAR check: Commerce Control List</div>
        <div className='export-design-inset export-design-inset-text'>
          <b>No entry matched,</b> so it falls to EAR99.
          {candidateEccns.length > 0 && ` Closest checked: ${candidateEccns.join(', ')}.`}
        </div>
        {unusedBlock}
      </section>
    );
  } else if (regime === 'ITAR') {
    earLane = (
      <section>
        <div className='export-design-section-h'>EAR check: not needed</div>
        <div className='export-design-inset export-design-inset-text'>
          An item on the USML stays under ITAR, so the Commerce Control List doesn&apos;t apply to it. Ask
          what it would take to move it to the EAR and that part gets checked then.
        </div>
        {earRows.length > 0 && itarRows.length > 0 && <SpecRows rows={earRows} />}
      </section>
    );
  } else if (rows.length > 0) {
    earLane = (
      <section>
        <div className='export-design-section-h'>Controls it may meet</div>
        {evidenceBlock}
        <SpecRows rows={rows} />
        {unusedBlock}
      </section>
    );
  }

  return (
    <div className='export-design-result'>
      {card.intro && (
        <div className='export-design-result-intro'>
          <Markdown text={card.intro} />
        </div>
      )}
      <article className='export-design-block' aria-label='Classification result'>
        <div className='export-design-block-h'>
          <div>
            <div className='export-design-block-kicker'>
              {card.status === 'preliminary' ? 'Classification, second review under way' : 'Classification'}
            </div>
            <h3 className='export-design-block-title'>
              {headline.title}
              {headline.code && <span className='export-design-code'> {headline.code}</span>}
            </h3>
            <div className='export-design-block-sub'>{headline.sub}</div>
          </div>
          <div className='export-design-block-pills'>
            <span className={`export-design-pill ${REGIME_CLASS[regime]}`}>{regime === 'uncertain' ? 'Uncertain' : regime}</span>
            <span className={`export-design-pill ${STATUS_CLASS[card.status] || 'is-neutral'}`}>
              {STATUS_LABELS[card.status]}
            </span>
          </div>
        </div>

        {claim === true && <div className='export-design-claim'>This confirms what you expected.</div>}
        {claim === false && (
          <div className='export-design-claim'>This differs from what you expected. The reasons are below.</div>
        )}

        <div className='export-design-track' aria-label='Where this product sits'>
          {track.map((segment) => (
            <div
              key={segment.id}
              className={`export-design-track-seg${segment.here ? ` is-here ${REGIME_CLASS[regime]}` : ''}`}
              aria-current={segment.here ? 'true' : undefined}
            >
              <div className='t'>{segment.title}</div>
              <div className='v'>{segment.value}</div>
            </div>
          ))}
        </div>

        {/* S6: the Preliminary card sent back by the second review, and what the review changed. S12 R-8:
            what the second review is, what it found and why the result changed, in plain words. */}
        {card.status === 'preliminary' && !card.rechecking && (
          <div className='export-design-review-note'>{SECOND_REVIEW_NOTE}</div>
        )}
        {card.status === 'preliminary' && card.rechecking && (
          <div className='export-design-warn'>
            <b>The second review asked for corrections. Re-checking now: this result may change.</b>
            {card.recheck_reason && (
              <div className='export-design-review-why'>What it found: {cardText(card.recheck_reason)}</div>
            )}
          </div>
        )}
        {card.status !== 'preliminary' && card.revision && (
          <div className='export-design-revision'>
            {cardText(card.revision)}
            {card.revision_reason && (
              <div className='export-design-review-why'>Why it changed: {cardText(card.revision_reason)}</div>
            )}
          </div>
        )}

        {card.status === 'provisional' && (
          <div className='export-design-warn'>
            <b>An independent review flagged concerns. Treat this as provisional.</b>
            {issues.length > 0 && (
              <ul>
                {issues.map((issue) => (
                  <li key={issue}>{issue}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Unsettled: what it hinges on comes first, then the lanes. */}
        {regime === 'uncertain' && assumptionsBlock}
        {itarLane}
        {earLane}
        {regime !== 'uncertain' && assumptionsBlock}

        {card.licensing_line && (
          <div className='export-design-inset export-design-inset-text export-design-licensing'>
            <b>For licensing:</b> {card.licensing_line}
            {card.licensing_note && (
              <div className='export-design-licensing-note'>
                <Markdown text={card.licensing_note} />
              </div>
            )}
          </div>
        )}

        <details className='export-design-more'>
          <summary>How we got here</summary>
          <ol className='export-design-trail'>
            {trailOf(card).map((step) => (
              <li key={step.step}>
                <span>{step.step}</span>
                <span>{step.text}</span>
              </li>
            ))}
          </ol>
          {(regime === 'ITAR' || screenReasons.length > 0) && orderNote && (
            <div className='export-design-trail-note'>{orderNote}</div>
          )}
          {confidence && <div className='export-design-trail-note'>Confidence: {confidence}.</div>}
          {explanation && (
            <div className='export-design-explanation'>
              <Markdown text={explanation} />
            </div>
          )}
        </details>
        {liveFoot}
      </article>
    </div>
  );
};

export default ExportDesignResultCard;
